import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { api, setToken } from "./api";

interface AuthState {
  token: string | null;
  username: string | null;
  /** Eigene Mail-Adresse (Login-Konto). Wird gebraucht, um sich selbst NICHT als
   *  Teilnehmer einladen zu koennen — der Organisator ist man serverseitig
   *  ohnehin (`user.owner_ref`, app/routers/calendar.py).
   *
   *  `null` heisst „noch nicht bekannt" (Hydrierung laeuft, oder /auth/me nicht
   *  erreichbar) und NICHT „es gibt keine". Wer die Regel darauf stuetzt, muss
   *  den Fall aushalten: ohne Adresse gibt es keinen Vergleich, also auch keine
   *  Filterung — lieber einmal die eigene Adresse in der Liste als eine
   *  Filterung, die auf Verdacht den falschen Menschen entfernt. */
  email: string | null;
  /** Adresse, die `/auth/me` **in dieser Sitzung erfolgreich bestätigt** hat.
   *
   *  Unterschied zu `email`: die dort steht auch aus dem `localStorage` — also
   *  ein Wert, den zuletzt irgendwann irgendwer bestätigt hat. `confirmedEmail`
   *  wird NIE persistiert und ist nach jedem Laden erst dann gesetzt, wenn der
   *  Server geantwortet hat. Scheitert die Antwort (401/503/offline) oder nennt
   *  sie keine Adresse, bleibt der Wert `null` — OHNE `email` anzutasten.
   *
   *  Wer eine Anzeige davon abhängig macht, bekommt „nur nach frischer
   *  Bestätigung" (heute: die Studio-Gruppe im Menü, studioAccess.ts). Wer die
   *  zuletzt bekannte Adresse braucht, nimmt weiter `email` (heute: der Riegel
   *  „sich nicht selbst einladen", CalendarMs.tsx:2949 → participants.ts:82 —
   *  der darf bei einem Serverwackler NICHT ausgehen). */
  confirmedEmail: string | null;
  /** Atlas-Rolle, die `/auth/me` **in dieser Sitzung bestätigt** hat (Mig 0095).
   *
   *  Dieselbe Strenge wie `confirmedEmail` und aus demselben Grund: nie
   *  persistiert, nach jedem Laden erst gesetzt, wenn der Server geantwortet
   *  hat. Ein Wert aus dem `localStorage` hiesse „Admin, weil hier mal Admin
   *  stand" — und ein Admin-Reiter, der sich selbst bestätigt, ist keiner.
   *
   *  `null` = kein Privileg (gewöhnlicher Nutzer, Serverwackler, offline). Die
   *  Richtung ist Absicht: im Zweifel NICHT anzeigen.
   *
   *  ⚠️ Anzeige, keine Grenze. Die Grenze ist `app/roles.py::require_admin` an
   *  den Endpoints. Wer diesen Wert im Browser fälscht, bekommt einen Reiter,
   *  dessen Knöpfe 403 liefern. */
  confirmedRole: string | null;
  /** Freigeschaltete Module, die `/auth/me` **in dieser Sitzung bestätigt** hat
   *  (Mig 0099: `users.modules`, Wertemenge `app/roles.py::MODULES`).
   *
   *  Dieselbe Strenge wie `confirmedRole` und aus demselben Grund: nie
   *  persistiert, nach jedem Laden erst gesetzt, wenn der Server geantwortet
   *  hat. Leeres Array = kein Zusatz-Modul (gewöhnlicher Nutzer, Serverwackler,
   *  offline). Die Richtung ist Absicht: im Zweifel NICHT anzeigen.
   *
   *  Ein Admin bekommt hier vom Server ALLE Module aufgezählt — das Menü muss
   *  die Regel „Rolle schliesst Module ein" nicht ein zweites Mal kennen.
   *
   *  ⚠️ Anzeige, keine Grenze. Die Grenze ist `app/roles.py::require_module` an
   *  den Endpoints. Wer diesen Wert im Browser fälscht, bekommt einen
   *  Menü-Eintrag, dessen Seite 403 lädt. */
  confirmedModules: string[];
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
}

/** Was /auth/me liefert (app/routers/auth.py:18). */
interface MeResponse {
  name: string;
  email?: string | null;
  role?: string | null;
  modules?: string[] | null;
}

/** Nur echte, nicht-leere Bezeichner — was der Server nicht als Liste von
 *  Strings liefert, ist kein Recht. (Der Server filtert bereits gegen
 *  `MODULES`; hier steht nur, dass eine kaputte Antwort nach „nichts" fällt
 *  und nicht nach `[undefined]`.) */
function moduleListe(roh: unknown): string[] {
  if (!Array.isArray(roh)) return [];
  return roh
    .map((m) => (typeof m === "string" ? m.trim().toLowerCase() : ""))
    .filter(Boolean);
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tok, setTok] = useState<string | null>(() => localStorage.getItem("sb_token"));
  const [username, setUsername] = useState<string | null>(
    () => localStorage.getItem("sb_username")
  );
  const [email, setEmail] = useState<string | null>(
    () => localStorage.getItem("sb_email")
  );
  // Bewusst OHNE localStorage-Initialisierung: „bestätigt" ist eine Aussage über
  // DIESE Sitzung. Käme der Startwert aus dem Browser, wäre der Begriff genau
  // das, was er nicht sein soll — ein alter Wert, der sich selbst bestätigt.
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  // Ebenfalls bewusst OHNE localStorage — s. `confirmedRole` in `AuthState`.
  const [confirmedRole, setConfirmedRole] = useState<string | null>(null);
  // Und ebenso die Module (Mig 0099) — s. `confirmedModules` in `AuthState`.
  const [confirmedModules, setConfirmedModules] = useState<string[]>([]);
  // Für welches Token die Selbstauskunft schon lief. Verhindert den zweiten
  // Abruf direkt nach `login()` (das fragt selbst) — gemessen 2 Abrufe im
  // Abstand von ~120 ms.
  const bestaetigtFuerTok = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setToken(tok);
  }, [tok]);

  // Selbstauskunft EINMAL pro Sitzung frisch holen, solange ein Token da ist —
  // nicht nur dann, wenn Name oder Adresse im localStorage fehlen. Die frühere
  // Bedingung `(!username || !email)` liess einen einmal gespeicherten Wert
  // unbefristet gelten: wer `sb_email` im Browser stehen hatte, behielt sie,
  // auch wenn der Server inzwischen eine andere Person hinter dem Token sah
  // (gemessen: 0 Abrufe, Server sagte „eva", die alte Adresse blieb stehen).
  //
  // Das Ergebnis landet in `confirmedEmail` — und NUR dort. `email` und
  // `sb_email` werden ausschliesslich bei einer ERFOLGREICHEN Antwort mit
  // Adresse fortgeschrieben; ein Fehlschlag löscht nichts.
  //
  // Warum nicht löschen: `email` ist die zuletzt bekannte Adresse und trägt
  // fremde Regeln, allen voran den Riegel „man kann sich nicht selbst einladen"
  // (CalendarMs.tsx:2949 → participants.ts:82: `if (!selfEmail) return list;`).
  // Eine frühere Fassung löschte hier bei jedem Fehler — damit schaltete EIN
  // Serverwackler (503) diesen Riegel ab, und wegen `localStorage.removeItem`
  // blieb er über Reloads aus. Strenge, die für das Menü gedacht war, hätte so
  // eine fremde, gebaute Regel entwaffnet.
  //
  // Deps NUR `[tok]`: der Effekt setzt username/email selbst; stünden sie in den
  // Deps, riefe sich das Ganze im Kreis.
  useEffect(() => {
    if (!tok) {
      setConfirmedEmail(null);
      setConfirmedRole(null);
      setConfirmedModules([]);
      bestaetigtFuerTok.current = null;
      return;
    }
    if (bestaetigtFuerTok.current === tok) return; // login() hat gerade gefragt
    let cancelled = false;
    api
      .get<MeResponse>("/auth/me")
      .then((me) => {
        if (cancelled) return;
        bestaetigtFuerTok.current = tok;
        if (me?.name) {
          setUsername(me.name);
          localStorage.setItem("sb_username", me.name);
        }
        const addr = (me?.email || "").trim();
        // Bestätigt ist nur, was der Server JETZT genannt hat.
        setConfirmedEmail(addr || null);
        setConfirmedRole((me?.role || "").trim() || null);
        setConfirmedModules(moduleListe(me?.modules));
        if (addr) {
          setEmail(addr);
          localStorage.setItem("sb_email", addr);
        }
      })
      .catch(() => {
        // 401/503/offline: nichts ist bestätigt — aber auch nichts verworfen.
        if (cancelled) return;
        setConfirmedEmail(null);
        setConfirmedRole(null);
        setConfirmedModules([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tok]);

  const login = useCallback(async (newTok: string) => {
    setLoading(true);
    try {
      // Setze den Token VORHER (api liest aus localStorage), dann frag /auth/me
      // — so kennen wir direkt den korrekten Username vom Server.
      setToken(newTok);
      const me = await api.get<MeResponse>("/auth/me");
      // Diese Antwort IST die frische Bestätigung — den Merker setzen, bevor
      // `setTok` den Effekt oben auslöst, sonst fragt der gleich nochmal.
      bestaetigtFuerTok.current = newTok;
      setTok(newTok);
      setUsername(me.name);
      localStorage.setItem("sb_token", newTok);
      localStorage.setItem("sb_username", me.name);
      const addr = (me.email || "").trim();
      setEmail(addr || null);
      setConfirmedEmail(addr || null);
      setConfirmedRole((me.role || "").trim() || null);
      setConfirmedModules(moduleListe(me.modules));
      // Beim ausdrücklichen Anmelden darf die gespeicherte Adresse weg: hier
      // hat der Server gerade erfolgreich geantwortet und gesagt, es gebe keine.
      if (addr) localStorage.setItem("sb_email", addr);
      else localStorage.removeItem("sb_email");
    } catch (e) {
      setToken(null);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setTok(null);
    setUsername(null);
    setEmail(null);
    setConfirmedEmail(null);
    setConfirmedRole(null);
    setConfirmedModules([]);
    bestaetigtFuerTok.current = null;
    localStorage.removeItem("sb_token");
    localStorage.removeItem("sb_username");
    localStorage.removeItem("sb_email");
  }, []);

  return (
    <AuthCtx.Provider
      value={{
        token: tok,
        username,
        email,
        confirmedEmail,
        confirmedRole,
        confirmedModules,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
